import { useDroppable } from '@dnd-kit/core'
import { FileText, GripVertical, Plus, type LucideIcon } from 'lucide-react'

import type { FormField } from '@/types/api/form'

import { FIELD_CONFIG } from './fieldTypeConfig'

export function DragOverlayCard({ cfg }: { cfg: { icon: LucideIcon; label: string; bg: string; iconColor: string } }) {
  const Icon = cfg.icon
  return <div className={`flex items-center gap-2 rounded-lg border border-border px-3 py-2 ${cfg.bg}`}><Icon className={`h-4 w-4 ${cfg.iconColor}`} /><span className={`text-sm font-medium ${cfg.iconColor}`}>{cfg.label}</span></div>
}

export function CanvasFieldCardOverlay({ field }: { field: FormField }) {
  const cfg = FIELD_CONFIG[field.type]
  const Icon = cfg.icon
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 text-foreground">
      <GripVertical className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${cfg.bg}`}><Icon className={`h-4 w-4 ${cfg.iconColor}`} /></div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-2"><span className={`text-sm font-medium ${cfg.iconColor}`}>{cfg.label}</span>{field.required && <span className="text-xs text-destructive">Required</span>}</div>
        <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground"><span>Key: {field.key}</span><span>Label: {field.label}</span></div>
      </div>
    </div>
  )
}

export function DropIndicator() {
  return <div className="h-0.5 rounded-full bg-primary" />
}

function DropZone({ id, active, position }: { id: string; active: boolean; position: 'top' | 'end' }) {
  const { setNodeRef, isOver } = useDroppable({ id })
  const highlighted = active || isOver
  return <div ref={setNodeRef} className={`${position === 'top' ? 'mb-1' : 'mt-1'} h-8 rounded-md border-2 border-dashed transition-colors ${highlighted ? 'border-primary bg-primary/10' : 'border-border bg-transparent'}`} />
}

export function CanvasTopDropZone({ isOver }: { isOver: boolean }) {
  return <DropZone id="canvas-top" active={isOver} position="top" />
}

export function CanvasEndDropZone({ isOver }: { isOver: boolean }) {
  const { setNodeRef, isOver: pointerIsOver } = useDroppable({ id: 'canvas-end' })
  const highlighted = isOver || pointerIsOver
  return (
    <div ref={setNodeRef} className={`mt-1 hidden min-h-24 items-center justify-center gap-3 rounded-lg border-2 border-dashed transition-colors md:flex ${highlighted ? 'border-primary bg-primary/10' : 'border-border bg-muted/30'}`}>
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary"><Plus className="h-5 w-5" /></span>
      <div><p className="text-sm font-medium text-foreground">{highlighted ? 'Drop fields here' : 'Drag and drop fields here'}</p><p className="text-xs text-muted-foreground">or click a field type to add it</p></div>
    </div>
  )
}

export function CanvasEmptyZone() {
  const { setNodeRef, isOver } = useDroppable({ id: 'canvas-droppable' })
  return (
    <div ref={setNodeRef} className={`flex min-h-72 flex-1 flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${isOver ? 'border-primary bg-primary/10' : 'border-border bg-muted/30'}`}>
      <div className={`rounded-xl p-4 ${isOver ? 'bg-primary/10' : 'bg-muted'}`}><FileText className={`h-7 w-7 ${isOver ? 'text-primary' : 'text-muted-foreground'}`} /></div>
      <p className="mt-3 text-sm font-medium text-muted-foreground"><span className="md:hidden">Tap Add field to get started</span><span className="hidden md:inline">{isOver ? 'Drop to add field' : 'Drag a field type from the left panel'}</span></p>
      <p className="hidden text-xs text-muted-foreground md:block">or click a field type to add it</p>
    </div>
  )
}
